import React from 'react';

import { ENTITLEMENT_TIERS } from 'components/entitlements/constants';

import TeamsFlyoutMenu, { TeamsMobileMenu } from './TeamsFlyoutMenu';

export const ANALYTICS_SUBMENU = [
  {
    name: 'Analytics',
    header: true,
    requiredRoles: ['viewer', 'collaborator', 'member', 'owner'],
  },
  {
    name: 'MTTX Analytics',
    href: '/analytics/mttx_analytics/services',
    keepQuery: true,
    requiredRoles: ['viewer', 'collaborator', 'member', 'owner'],
    requiredPermission: 'read_analytics',
  },
  {
    name: 'Alerts',
    href: '/analytics/alerts',
    keepQuery: true,
    requiredRoles: ['viewer', 'collaborator', 'member', 'owner'],
    flag: 'signals',
    requiredPermission: 'read_analytics',
  },
  {
    name: 'On-Call Hours',
    href: '/analytics/shift_hours',
    requiredRoles: ['collaborator', 'member', 'owner'],
    flag: 'signals',
    requiredPermission: 'read_analytics',
  },
  {
    name: 'Incidents and Impact',
    href: '/analytics/incidents_impacts',
    keepQuery: true,
    requiredRoles: ['viewer', 'collaborator', 'member', 'owner'],
    requiredPermission: 'read_analytics',
  },
  {
    name: 'Resources and Tasks',
    href: '/analytics/resources_tasks',
    keepQuery: true,
    requiredRoles: ['viewer', 'collaborator', 'member', 'owner'],
    requiredPermission: 'read_analytics',
  },
];

export const INCIDENTS_SUBMENU = [
  {
    name: 'Incidents',
    header: true,
    requiredRoles: ['viewer', 'collaborator', 'member', 'owner'],
  },
  {
    name: 'All Incidents',
    href: '/incidents',
    requiredRoles: ['viewer', 'collaborator', 'member', 'owner'],
    exactMatch: true,
    requiredPermission: 'read_incidents',
  },
  {
    name: 'Tasks',
    href: '/incidents/tasks',
    requiredRoles: ['viewer', 'collaborator', 'member', 'owner'],
    requiredPermission: 'read_incidents',
  },
  {
    name: 'Follow Ups',
    href: '/incidents/follow_ups',
    requiredRoles: ['viewer', 'collaborator', 'member', 'owner'],
    requiredPermission: 'read_incidents',
  },
  {
    name: 'Scheduled Maintenances',
    href: '/incidents/maintenances',
    requiredRoles: ['viewer', 'collaborator', 'member', 'owner'],
    requiredPermission: 'read_incidents',
  },
];

export const SIGNALS_SUBMENU = [
  {
    name: 'Signals',
    header: true,
    requiredRoles: ['viewer', 'collaborator', 'member', 'owner'],
  },
  {
    name: 'Alerts',
    href: '/signals/alerts',
    requiredRoles: ['viewer', 'collaborator', 'member', 'owner'],
    requiredPermission: 'read_alerts',
  },
  {
    name: 'Event Logs',
    href: '/signals/logs',
    requiredRoles: ['viewer', 'collaborator', 'member', 'owner'],
    requiredPermission: 'read_alerts',
  },
  {
    name: 'Event Sources',
    href: '/signals/sources',
    requiredRoles: ['member', 'owner'],
    requiredPermission: 'read_event_sources',
  },
  {
    name: 'On-Call Schedules',
    href: '/signals/on_call_schedules',
    requiredRoles: ['viewer', 'collaborator', 'member', 'owner'],
    requiredPermission: 'read_on_call_schedules',
  },
  {
    name: 'Alert Grouping',
    href: '/signals/alert_grouping',
    requiredRoles: ['member', 'owner'],
    flag: 'alertGrouping',
    requiredPermission: 'read_alert_grouping',
  },
  {
    name: 'Alert Visualization',
    href: '/signals/alert_visualization',
    requiredRoles: ['viewer', 'collaborator', 'member', 'owner'],
    requiredPermission: 'read_alerts',
  },
  {
    name: 'Webhook Targets',
    href: '/signals/webhook_targets',
    requiredRoles: ['member', 'owner'],
    requiredPermission: 'read_webhook_targets',
  },
  {
    name: 'Global Notification Policy',
    href: '/signals/notification_policy',
    requiredRoles: ['owner'],
    requiredPermission: 'manage_organization_notification_policies',
  },
];

export const CATALOG_SUBMENU = [
  {
    name: 'Catalog',
    header: true,
    requiredRoles: ['viewer', 'collaborator', 'member', 'owner'],
  },
  {
    name: 'Services',
    href: '/catalog/services',
    requiredRoles: ['viewer', 'collaborator', 'member', 'owner'],
    exactMatch: true,
    requiredPermission: 'read_service_catalog',
  },
  {
    name: 'Functionalities',
    href: '/catalog/functionalities',
    requiredRoles: ['viewer', 'collaborator', 'member', 'owner'],
    requiredPermission: 'read_service_catalog',
  },

  {
    name: 'Environments',
    href: '/catalog/environments',
    requiredRoles: ['viewer', 'collaborator', 'member', 'owner'],
    requiredPermission: 'read_service_catalog',
  },
  {
    name: 'Readiness Checklists',
    href: '/catalog/checklists',
    requiredRoles: ['viewer', 'collaborator', 'member', 'owner'],
    requiredPermission: 'read_service_catalog',
  },
  {
    name: 'Change events',
    href: '/catalog/change_events',
    requiredRoles: ['viewer', 'collaborator', 'member', 'owner'],
    requiredPermission: 'read_service_catalog',
  },
  {
    name: 'Catalog settings',
    href: '/catalog/services/catalog_settings',
    requiredRoles: ['member', 'owner'],
    requiredPermission: 'read_service_catalog',
  },
];

export const SETTINGS_SUBMENU = [
  {
    name: 'General',
    header: true,
    requiredRoles: ['viewer', 'collaborator', 'member', 'owner'],
  },
  {
    name: 'Organization',
    href: '/settings/organization',
    requiredRoles: ['owner'],
    exactMatch: true,
    requiredPermission: 'read_organization_settings',
  },
  {
    name: '✨ AI',
    href: '/settings/ai',
    requiredRoles: ['owner'],
    flag: 'integrationsSlackAiSummary',
    requiredPermission: 'read_organization_settings',
  },
  {
    name: 'Users',
    href: '/settings/users',
    requiredRoles: ['owner'],
    requiredPermission: 'read_users',
  },
  {
    name: 'Roles and Permissions',
    href: '/settings/organization_roles',
    requiredRoles: ['owner'],
    requiredPermission: 'read_roles',
  },
  {
    name: 'Teams',
    href: '/settings/teams',
    hideFlag: 'signals',
    requiredRoles: ['viewer', 'collaborator', 'member', 'owner'],
    requiredPermission: 'read_teams',
  },
  {
    name: 'Single sign on',
    href: '/settings/sso/edit',
    requiredRoles: ['owner'],
    requiredPermission: 'manage_organization_settings',
  },
  {
    name: 'Billing and Usage',
    href: '/settings/billing',
    requiredRoles: ['owner'],
    requiredPermission: 'read_organization_settings',
  },
  {
    name: 'Incidents',
    header: true,
    requiredRoles: ['viewer', 'collaborator', 'member', 'owner'],
  },
  {
    name: 'Incident milestones',
    href: '/settings/incident_milestones',
    requiredRoles: ['member', 'owner'],
    flag: 'customMilestones',
    requiredPermission: 'read_incident_settings',
  },
  {
    name: 'Incident types and settings',
    href: '/settings/incident_types_and_settings',
    requiredRoles: ['viewer', 'collaborator', 'member', 'owner'],
    requiredPermission: 'read_incident_settings',
  },
  {
    name: 'Incident roles',
    href: '/settings/roles',
    requiredRoles: ['viewer', 'collaborator', 'member', 'owner'],
    requiredPermission: 'read_incident_settings',
  },
  {
    name: 'Audiences',
    href: '/settings/audiences',
    requiredRoles: ['viewer', 'collaborator', 'member', 'owner'],
    flag: 'incidentAudiences',
    requiredPermission: 'read_audiences',
  },
  {
    name: 'Severities',
    href: '/settings/severities_index',
    requiredRoles: ['viewer', 'collaborator', 'member', 'owner'],
    requiredPermission: 'read_incident_settings',
  },
  {
    name: 'Priorities',
    href: '/settings/priorities',
    requiredRoles: ['viewer', 'collaborator', 'member', 'owner'],
    requiredPermission: 'read_incident_settings',
  },
  {
    name: 'Conditions',
    href: '/settings/conditions',
    requiredRoles: ['viewer', 'collaborator', 'member', 'owner'],
    requiredPermission: 'read_incident_settings',
  },
  {
    name: 'Severities matrix',
    href: '/settings/severities_matrix',
    requiredRoles: ['viewer', 'collaborator', 'member', 'owner'],
    requiredPermission: 'read_incident_settings',
  },
  {
    name: 'Status templates',
    href: '/settings/status_update_templates',
    requiredRoles: ['viewer', 'collaborator', 'member', 'owner'],
    requiredPermission: 'read_status_templates',
  },
  {
    name: 'Task lists',
    href: '/settings/task_lists',
    requiredRoles: ['viewer', 'collaborator', 'member', 'owner'],
    requiredPermission: 'read_incident_settings',
  },
  {
    name: 'Ticketing settings',
    href: '/settings/tickets',
    requiredRoles: ['owner'],
    requiredPermission: 'read_incident_settings',
  },
  {
    name: 'Retrospective templates',
    href: '/settings/retrospectives/templates',
    requiredRoles: ['member', 'owner'],
    requiredPermission: 'read_incident_settings',
  },
  {
    name: 'Report templates',
    href: '/settings/incident_reports_templates',
    requiredRoles: ['member', 'owner'],
    requiredPermission: 'read_incident_settings',
    flag: 'incidentReports',
  },
  {
    name: 'Integrations',
    header: true,
    requiredRoles: ['owner'],
  },
  {
    name: 'Integration List',
    iconName: 'integrations',
    href: '/settings/integrations',
    requiredRoles: ['owner'],
    exclusions: ['/settings/integrations/webhooks'],
    requiredPermission: 'read_integrations',
  },
  {
    name: 'API Keys',
    href: '/settings/api_keys',
    requiredRoles: ['owner'],
    requiredPermission: 'read_api_keys',
  },
  {
    name: 'Webhooks',
    href: '/settings/integrations/webhooks',
    requiredRoles: ['owner'],
    requiredPermission: 'read_webhooks',
  },
  {
    name: 'Secrets',
    href: '/settings/secrets',
    requiredRoles: ['owner'],
    requiredPermission: 'read_organization_secrets',
  },
  {
    name: 'Audit logs',
    href: '/settings/audit_events',
    requiredRoles: ['owner'],
    flag: 'auditEvents',
    requiredPermission: 'read_audit_events',
  },
];

export const PROFILE_SUBMENU = [
  {
    name: 'User Settings',
    header: true,
    requiredRoles: ['viewer', 'collaborator', 'member', 'owner'],
  },
  {
    name: 'Profile',
    href: '/account/edit',
    requiredRoles: ['viewer', 'collaborator', 'member', 'owner'],
  },
  {
    name: 'Password & Security',
    href: '/account/password_security',
    requiredRoles: ['viewer', 'collaborator', 'member', 'owner'],
  },
  {
    name: 'Organizations',
    header: true,
    requiredRoles: ['viewer', 'collaborator', 'member', 'owner'],
  },
];

export const USER_SUBMENU = [
  {
    name: 'Profile',
    href: '/account/edit',
    requiredRoles: ['viewer', 'collaborator', 'member', 'owner'],
  },
  {
    name: 'API documentation',
    href: 'https://developers.firehydrant.com/',
    requiredRoles: ['viewer', 'collaborator', 'member', 'owner'],
    externalLink: true,
  },
  {
    name: 'Help center',
    href: 'https://firehydrant.com/docs',
    requiredRoles: ['viewer', 'collaborator', 'member', 'owner'],
    externalLink: true,
  },
  {
    name: 'Changelog',
    href: 'https://firehydrant.com/changelog/',
    requiredRoles: ['viewer', 'collaborator', 'member', 'owner'],
    externalLink: true,
  },
  {
    name: 'Sign out',
    href: '/sessions',
    requiredRoles: ['viewer', 'collaborator', 'member', 'owner'],
    extras: {
      href: '/sessions',
      rel: 'nofollow',
      boxShadow: 'none !important',
      textDecoration: 'none !important',
      'data-method': 'delete',
    },
  },
];

export const HORIZONTAL_NAV_STRUCTURE = [
  {
    route: '/overview',
    displayName: 'Home',
    hideFlag: 'signals',
    requiredPermission: 'read_alerts',
  },
  {
    route: '/signals',
    displayName: 'Signals',
    subMenuItems: SIGNALS_SUBMENU,
    flag: 'signals',
    requiredPermission: 'read_alerts',
  },
  {
    route: '/incidents',
    displayName: 'Incidents',
    subMenuItems: INCIDENTS_SUBMENU,
    requiredPermission: 'read_incidents',
  },
  {
    route: '/runbooks',
    displayName: 'Runbooks',
    requiredPermission: 'read_runbooks',
  },
  {
    displayName: 'Status pages',
    route: '/status_pages',
    requiredPermission: 'read_integrations',
  },
  {
    route: '/catalog',
    displayName: 'Catalog',
    subMenuItems: CATALOG_SUBMENU,
    requiredPermission: 'read_service_catalog',
  },
  {
    route: '/analytics',
    displayName: 'Analytics',
    subMenuItems: ANALYTICS_SUBMENU,
    requiredPermission: 'read_analytics',
  },
  {
    route: '/settings/teams',
    displayName: 'Teams',
    flyOutMenu: (onClose) => <TeamsFlyoutMenu onClose={onClose} />,
    dynamicMobileSubMenu: () => <TeamsMobileMenu />,
    flag: 'signals',
    requiredPermission: 'read_teams',
  },
  {
    displayName: 'Settings',
    route: '/settings',
    subMenuItems: SETTINGS_SUBMENU,
    hideFlag: 'signals',
  },
  {
    displayName: 'Settings',
    route: '/settings',
    subMenuItems: SETTINGS_SUBMENU,
    flag: 'signals',
    isMobileOnly: true,
  },
  {
    displayName: 'User',
    isMobileOnly: true,
    navIcon: true,
    subMenuItems: USER_SUBMENU,
  },
];

// This is being used for Breadcrumbs and helmet text
export default (internalTools, copySupportInfo = () => {}) => [
  [
    {
      name: 'Home',
      href: '/overview',
      ownerOnly: false,
      iconName: 'home',
    },
    {
      name: 'Signals',
      href: '/signals',
      ownerOnly: false,
      iconName: 'incidents',
      flag: 'signals',
    },
    {
      name: 'Incidents',
      href: '/incidents',
      ownerOnly: false,
      iconName: 'incidents',
    },
    {
      name: 'Incident response',
      ownerOnly: false,
      iconName: 'incidentResponse',
      subMenu: [
        {
          name: 'Scheduled maintenances',
          href: '/scheduled_maintenances',
          ownerOnly: false,
        },
        {
          name: 'Task Lists',
          href: '/task_lists',
          ownerOnly: false,
          badge: true,
        },
        {
          name: 'Teams',
          href: '/teams',
          ownerOnly: false,
        },
        {
          name: 'Roles',
          href: '/incident_roles',
          ownerOnly: false,
        },
        {
          name: 'Severities',
          href: '/organizations/severities',
          ownerOnly: false,
        },
        {
          name: 'Retrospective templates',
          href: '/settings/retrospectives/templates',
          requiredRoles: ['member', 'owner'],
          ownerOnly: true,
        },
        {
          name: 'Report templates',
          href: '/settings/incident_reports_templates',
          requiredRoles: ['member', 'owner'],
          ownerOnly: true,
          flag: 'incidentReports',
        },
        {
          name: 'Incident types',
          href: '/incident_types',
          ownerOnly: false,
          hideFlag: 'incidentTypesRequiredFields',
        },
        {
          name: 'Incident types and settings',
          href: '/incident_types_and_settings',
          ownerOnly: false,
          flag: 'incidentTypesRequiredFields',
        },
        {
          name: 'Ticketing settings',
          href: '/ticketing_settings',
          ownerOnly: true,
        },
      ],
    },
    {
      name: 'Runbooks',
      href: '/runbooks',
      ownerOnly: false,
      iconName: 'runbooks',
    },
  ],
  [
    {
      name: 'Service catalog',
      ownerOnly: false,
      iconName: 'infrastructure',
      subMenu: [
        {
          name: 'Services',
          href: '/services',
          ownerOnly: false,
        },
        {
          name: 'Functionalities',
          href: '/functionalities',
          ownerOnly: false,
        },

        {
          name: 'Environments',
          href: '/environments',
          ownerOnly: false,
        },
        {
          name: 'Readiness Checklists',
          href: '/checklists',
          ownerOnly: false,
        },
        {
          name: 'Change events',
          href: '/change_events',
          ownerOnly: false,
        },
      ],
    },
    {
      name: 'Status pages',
      iconName: 'statusPages',
      href: '/organizations/nunc',
      ownerOnly: true,
    },
    {
      name: 'Analytics',
      version: 1,
      iconName: 'analytics',
      href: '/analytics/basic',
      flag: 'fdnsAnalyticsRedesignEndpoints',
      ownerOnly: false,
    },
    {
      name: 'Analytics',
      version: 2,
      iconName: 'analytics',
      ownerOnly: false,
      flag: 'fdnsAnalyticsRedesignEndpoints',
      badge: true,
      subMenu: [
        {
          name: 'Incidents and Impact',
          href: '/analytics/incidents_and_impact',
          ownerOnly: false,
          tierRestriction: [ENTITLEMENT_TIERS.enterprise],
        },
        {
          name: 'Impact Statistics',
          href: '/analytics/mttx_analytics',
          ownerOnly: false,
          tierRestriction: [ENTITLEMENT_TIERS.enterprise],
          isBeta: true,
        },
        {
          name: 'Resources and Tasks',
          href: '/analytics/resources_and_tasks',
          ownerOnly: false,
          tierRestriction: [ENTITLEMENT_TIERS.enterprise],
        },
        {
          name: 'Basic Analytics',
          iconName: 'analytics',
          href: '/analytics/basic',
          ownerOnly: false,
        },
      ],
    },
    {
      name: 'Integrations',
      iconName: 'integrations',
      href: '/organizations/integrations',
      ownerOnly: true,
    },
  ],
  [
    {
      name: 'Help',
      ownerOnly: false,
      iconName: 'help',
      subMenu: [
        {
          name: 'API documentation',
          href: 'https://developers.firehydrant.com',
          isExternalLink: true,
          ownerOnly: false,
          rel: 'noopener',
          target: '_blank',
        },
        {
          name: 'Help center',
          href: 'https://firehydrant.com/docs',
          isExternalLink: true,
          ownerOnly: false,
          rel: 'noopener',
          target: '_blank',
        },
        {
          name: 'Support info',
          onClick: copySupportInfo,
        },
        {
          name: 'Changelog',
          href: 'https://firehydrant.com/changelog/',
          isExternalLink: true,
          ownerOnly: false,
          rel: 'noopener',
          target: '_blank',
        },
      ],
    },
    {
      name: 'Organization',
      ownerOnly: true,
      iconName: 'organization',
      subMenu: [
        {
          name: 'Account overview',
          href: '/organizations/settings',
          ownerOnly: false,
        },
        {
          name: 'Users',
          href: '/organizations/members',
          ownerOnly: false,
        },
        {
          name: 'Bot users',
          href: '/organizations/bots',
          ownerOnly: true,
        },
        {
          name: 'Secrets',
          href: '/organizations/secrets',
          ownerOnly: true,
        },
        {
          name: 'Single sign on',
          href: '/organizations/sso/settings/edit',
          ownerOnly: false,
        },
      ],
    },
    {
      name: 'User',
      ownerOnly: false,
      iconName: 'user',
      subMenu: [
        {
          name: 'My account',
          href: '/account/edit',
          ownerOnly: false,
          hideStaffSession: true,
        },
        {
          name: 'Notification settings',
          href: '/account_notifications/edit',
          ownerOnly: false,
          hideStaffSession: true,
        },
        {
          name: 'Sign out',
          href: '/sessions',
          rel: 'nofollow',
          'data-method': 'delete',
          ownerOnly: false,
        },
      ],
    },
  ],
];
